import type { App, Project } from '@hangar/shared';
import type { Profile, ProviderAccount } from '@/api/schemas';
import { stagger } from '@/lib/motion';
import { RowActions, type RowAction } from '@/components/RowActions';
import { DefinedInBadge, ExecutionModeTag, PlatformIcon, Tag } from '@/components/badges';
import { ProviderBadge } from '@/components/ProviderBadge';
import { Card } from '@/components/ui/card';
import { bindingsLine, profileProviderKind } from './profileListModel';
import { RunProfileButton } from './RunProfileButton';

interface Props {
  profiles: Profile[];
  app: App;
  project: Project;
  accounts: ProviderAccount[] | undefined;
  actionsFor: (profile: Profile) => RowAction[];
}

/** Phone layout of the profiles panel: one card per profile. */
export function ProfilesPhoneList({ profiles, app, project, accounts, actionsFor }: Props) {
  return (
    <ul className="flex flex-col gap-3 p-4">
      {profiles.map((p, i) => (
        <li key={p.id} data-testid={`profile-row-${p.platform}-${p.slug}`} className="enter" style={stagger(i)}>
          <Card className="gap-2 bg-secondary/40 p-4 shadow-none">
            <div className="flex items-center gap-2">
              <PlatformIcon platform={p.platform} size={20} />
              <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-foreground">{p.name}</span>
              <DefinedInBadge definedIn={p.definedIn} />
            </div>
            <PhoneBadges p={p} accounts={accounts} />
            <div className="text-xs text-subtle">{bindingsLine(p, project)}</div>
            <div className="flex items-center gap-2 border-t pt-2">
              <RunProfileButton profile={p} app={app} project={project} />
              <RowActions actions={actionsFor(p)} subject={p.name} className="ml-auto" />
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
}

function PhoneBadges({ p, accounts }: { p: Profile; accounts: ProviderAccount[] | undefined }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Tag>{p.slug}</Tag>
      <ProviderBadge kind={profileProviderKind(p, accounts)} />
      <Tag className="uppercase">{p.artifactKind}</Tag>
      {p.executionModes.map((m) => (
        <ExecutionModeTag key={m} mode={m} />
      ))}
    </div>
  );
}
